import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { Search, Download, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { exportAttendanceReport } from '../../utils/pdfExport';

const Attendance = () => {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState({
    present: 0, late: 0, half_day: 0, absent: 0
  });

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const response = await api.get('/attendance/office/summary', { params });
      const data = response.data.attendance;
      setAttendance(data);

      setSummary({
        present: data.filter(a => a.status === 'Present').length,
        late: data.filter(a => a.status === 'Late').length,
        half_day: data.filter(a => a.status === 'Half Day').length,
        absent: data.filter(a => a.status === 'Absent').length,
      });

    } catch (err) {
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, [statusFilter, dateFrom, dateTo]);

  const getStatusBadge = (status) => {
    const styles = {
      'Present': 'bg-green-100 text-green-700',
      'Late': 'bg-yellow-100 text-yellow-700',
      'Half Day': 'bg-orange-100 text-orange-700',
      'Absent': 'bg-red-100 text-red-700',
    };
    const icons = {
      'Present': <CheckCircle size={12} />,
      'Late': <Clock size={12} />,
      'Half Day': <AlertTriangle size={12} />,
      'Absent': <XCircle size={12} />,
    };
    return (
      <span className={`flex items-center justify-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {icons[status]} {status}
      </span>
    );
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleTimeString('en-PK', {
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleDateString('en-PK', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '—';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  return (
    <Layout title="Attendance">

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-green-500">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-500 text-sm">Present</p>
              <p className="text-3xl font-bold text-gray-800">{summary.present}</p>
            </div>
            <CheckCircle size={32} className="text-green-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-yellow-500">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-500 text-sm">Late</p>
              <p className="text-3xl font-bold text-gray-800">{summary.late}</p>
            </div>
            <Clock size={32} className="text-yellow-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-orange-500">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-500 text-sm">Half Day</p>
              <p className="text-3xl font-bold text-gray-800">{summary.half_day}</p>
            </div>
            <AlertTriangle size={32} className="text-orange-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-red-500">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-500 text-sm">Absent</p>
              <p className="text-3xl font-bold text-gray-800">{summary.absent}</p>
            </div>
            <XCircle size={32} className="text-red-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Status</option>
          <option value="Present">Present</option>
          <option value="Late">Late</option>
          <option value="Half Day">Half Day</option>
          <option value="Absent">Absent</option>
        </select>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">From:</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">To:</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <button
          onClick={() => { setStatusFilter(''); setDateFrom(''); setDateTo(''); }}
          className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50"
        >
          Clear Filters
        </button>

        <button
          onClick={() => exportAttendanceReport(attendance, `${dateFrom || 'All'} to ${dateTo || 'All'}`)}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 ml-auto"
        >
          <Download size={16} />
          Export PDF
        </button>
      </div>

      {/* Attendance Table */}
      <div className="bg-white rounded-xl shadow">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">#</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Officer Name</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Role</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">District</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Date</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Entry</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Exit</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Duration</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Late By</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Corrected</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="text-center py-8 text-gray-500">
                    Loading attendance...
                  </td>
                </tr>
              ) : attendance.length === 0 ? (
                <tr>
                  <td colSpan="11" className="text-center py-8 text-gray-500">
                    No attendance records found
                  </td>
                </tr>
              ) : (
                attendance.map((record, index) => (
                  <tr key={record.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{record.full_name}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{record.role}</td>
                    <td className="px-4 py-3 text-gray-600">{record.district_name}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(record.date)}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{formatTime(record.first_entry_time)}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{formatTime(record.last_exit_time)}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{formatDuration(record.total_duration_minutes)}</td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {record.late_duration_minutes > 0
                        ? <span className="text-yellow-600">{formatDuration(record.late_duration_minutes)}</span>
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">{getStatusBadge(record.status)}</td>
                    <td className="px-4 py-3 text-center">
                      {record.is_manual_correction ? (
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">Corrected</span>
                      ) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
          Total: {attendance.length} records
        </div>
      </div>

    </Layout>
  );
};

export default Attendance;